import { CompanyReportService } from '@/services/kpi';
import { ICompanyReport } from '@/types/response';
import {
  Alert,
  Button,
  Card,
  ConfigProvider,
  DatePicker,
  Empty,
  Segmented,
  Select,
  Space,
  Spin,
  Table,
  Typography,
} from 'antd';
import moment, { Moment } from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useQuery } from 'react-query';
import { SURFACE_THEME } from '../Dashboard';
import { DemographicsPanel } from '../Dashboard/DemographicsCard';
import { formatNumber } from '../Dashboard/KpiChart';
import {
  Unit,
  articleSummary,
  formatValue,
  henkaSummary,
  lifestyleSummary,
  nouknowSummary,
  toReportBuckets,
} from './companyReport';
import { IndexChart } from './IndexChart';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

interface IProps {
  title?: string;
}

/** 期間の初期値（今週を含む 26 週） */
const DEFAULT_WEEKS = 26;

const UNIT_OPTIONS = [
  { label: '週次', value: 'week' },
  { label: '月次', value: 'month' },
];

type FamilyOption = { value: number; label: string };

const LoadError: React.FC<{ onRetry: () => void }> = ({ onRetry }) => (
  <Alert
    type='error'
    showIcon
    message='取得できませんでした'
    description={
      <Space direction='vertical' size={4}>
        <Text>数字が出ていないのは「0」ではなく不明です。</Text>
        <Button size='small' onClick={onRetry}>
          再読み込み
        </Button>
      </Space>
    }
  />
);

const Stat: React.FC<{ label: string; value: string; sub?: string }> = ({
  label,
  value,
  sub,
}) => (
  <div className='flex flex-col'>
    <Text type='secondary' className='text-[12px]'>
      {label}
    </Text>
    <span className='text-[24px] font-semibold leading-tight'>{value}</span>
    {sub && (
      <Text type='secondary' className='text-[12px]'>
        {sub}
      </Text>
    )}
  </div>
);

/** 割合の横棒（1色。値は文字でも出す） */
const ShareBar: React.FC<{ pct: number | null }> = ({ pct }) => (
  <div className='h-[8px] w-full rounded-full bg-[#F1F1F1] overflow-hidden'>
    <div
      className='h-full rounded-full'
      style={{ width: `${pct ?? 0}%`, background: '#2a78d6' }}
    />
  </div>
);

// ---- 下段の集計 ----------------------------------------------------------

const LifestyleCard: React.FC<{ report: ICompanyReport }> = ({ report }) => {
  const s = lifestyleSummary(report);

  return (
    <Card size='small' title='みんなの生活診断'>
      <div className='flex flex-wrap gap-xl'>
        <Stat
          label='要注意件数（累積）'
          value={formatValue(s.cumulativeFailed, 0, '件')}
          sub={`うち期間内 ${formatValue(s.failedInPeriod, 0, '件')}`}
        />
        <Stat
          label='期間内に回答した人'
          value={formatValue(s.respondents, 0, '人')}
          sub={`要注意あり ${formatValue(s.attentionRespondents, 0, '人')}（${formatValue(s.attentionPct, 1, '%')}）`}
        />
      </div>
      <Text type='secondary' className='text-[12px] block mt-sm'>
        要注意＝診断の判定が不合格だった回答。累積は集計を始めた週（2026/8/31〜）から。要注意ありは、診断ごとの期間内の最新回答で1つでも要注意がある人。
      </Text>
    </Card>
  );
};

const NouknowCard: React.FC<{ report: ICompanyReport }> = ({ report }) => {
  const s = nouknowSummary(report);

  return (
    <Card size='small' title='のうKNOW 判定の割合'>
      {s.total === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description='期間内の受検はありません'
        />
      ) : (
        <div className='flex flex-col gap-sm'>
          <Text type='secondary' className='text-[12px]'>
            受検者 {formatValue(s.total, 0, '人')}
            （記憶力の判定。期間内の最新の受検）
          </Text>
          {s.ranks.map((r) => (
            <div
              key={r.rank}
              className='grid grid-cols-[64px_1fr_96px] items-center gap-sm'
            >
              <span className='text-[12px]'>{r.label}</span>
              <ShareBar pct={r.pct} />
              <span className='text-[12px] text-right'>
                {formatValue(r.pct, 1, '%')}（{r.users}人）
              </span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

const HenkaCard: React.FC<{ report: ICompanyReport }> = ({ report }) => {
  const rows = henkaSummary(report);

  return (
    <Card size='small' title='変化検知エンジンの検知件数'>
      <Table
        size='small'
        rowKey='metric'
        pagination={false}
        dataSource={rows}
        columns={[
          { title: '指標', dataIndex: 'label', key: 'label' },
          {
            title: '良い変化',
            dataIndex: 'good',
            key: 'good',
            align: 'right',
            render: (v: number) => formatValue(v, 0, '件'),
          },
          {
            title: '注意',
            dataIndex: 'bad',
            key: 'bad',
            align: 'right',
            render: (v: number) => formatValue(v, 0, '件'),
          },
          {
            title: '週あたり',
            dataIndex: 'perWeek',
            key: 'perWeek',
            align: 'right',
            render: (v: number | null) => formatValue(v, 1, '件'),
          },
        ]}
      />
      <Text type='secondary' className='text-[12px] block mt-sm'>
        期間内に検知が動いた {report.henka.runWeeks}{' '}
        週の合計。1家族は週に1件まで。配信を止めている（シャドーラン）週の検知も含む。
      </Text>
    </Card>
  );
};

const ArticlesCard: React.FC<{ report: ICompanyReport }> = ({ report }) => {
  const groups = articleSummary(report);

  return (
    <Card size='small' title='団体コード限定記事の開封率'>
      {groups.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description='期間中に公開されていた限定記事はありません'
        />
      ) : (
        <div className='flex flex-col gap-lg'>
          {groups.map((g) => (
            <div key={g.categoryId ?? 'none'} className='flex flex-col gap-xs'>
              <div className='flex items-baseline justify-between gap-md'>
                <Text strong>{g.categoryName}</Text>
                <Text>
                  <Text type='secondary' className='text-[12px] mr-xs'>
                    平均開封率
                  </Text>
                  {formatValue(g.averagePct, 1, '%')}
                </Text>
              </div>
              <Table
                size='small'
                rowKey='articleId'
                pagination={false}
                dataSource={g.articles}
                columns={[
                  { title: '記事', dataIndex: 'title', key: 'title' },
                  {
                    title: '公開',
                    dataIndex: 'publicAt',
                    key: 'publicAt',
                    width: 110,
                    render: (v: string | null) =>
                      v ? moment(v).format('YYYY/MM/DD') : '—',
                  },
                  {
                    title: '開いた人',
                    dataIndex: 'openedUsers',
                    key: 'openedUsers',
                    align: 'right',
                    width: 96,
                    render: (v: number) => formatValue(v, 0, '人'),
                  },
                  {
                    title: '開封率',
                    dataIndex: 'openPct',
                    key: 'openPct',
                    align: 'right',
                    width: 96,
                    render: (v: number | null) => formatValue(v, 1, '%'),
                  },
                ]}
              />
            </div>
          ))}
        </div>
      )}
      <Text type='secondary' className='text-[12px] block mt-sm'>
        開封率＝期間内に記事を初めて開いた人 ÷ 対象家族のメンバー（
        {formatValue(report.members, 0, '人')}）。
        読了の記録は無いので「開いた」で数えています。
      </Text>
    </Card>
  );
};

// ---- 対象ユーザー（取得した時点の値） -------------------------------------

const pctOf = (value: number, base: number): number | null =>
  base > 0 ? Math.round((value / base) * 1000) / 10 : null;

/** 全体ダッシュボードの主要指標カードと同じ見た目（前日比・前週比は無し） */
const ActivityCards: React.FC<{ report: ICompanyReport }> = ({ report }) => {
  const { multiMemberFamilies, dau, wau, mau } = report.activity;
  const cards = [
    {
      label: '複数人家族率',
      value: formatNumber(
        pctOf(multiMemberFamilies, report.families.length),
        '%',
      ),
      hint: `複数人家族 ${multiMemberFamilies} ÷ 対象 ${report.families.length}家族`,
    },
    {
      label: 'DAU',
      value: formatNumber(dau),
      hint: '直近24時間にアプリを開いた人',
    },
    {
      label: 'WAU',
      value: formatNumber(wau),
      hint: '直近7日にアプリを開いた人',
    },
    {
      label: 'MAU',
      value: formatNumber(mau),
      hint: '直近30日にアプリを開いた人',
    },
    {
      label: 'Stickiness',
      value: formatNumber(pctOf(dau, mau), '%'),
      hint: 'DAU ÷ MAU',
    },
  ];

  return (
    <div className='grid grid-cols-2 md:grid-cols-5 gap-md'>
      {cards.map((c) => (
        <Card key={c.label} size='small'>
          <div className='text-fg-muted text-[12px]'>{c.label}</div>
          <div className='text-fg text-[22px] font-semibold leading-tight my-xs'>
            {c.value}
          </div>
          <div className='text-fg-muted text-[11px] mt-xs'>{c.hint}</div>
        </Card>
      ))}
    </div>
  );
};

// ---- 画面 ---------------------------------------------------------------

export const CompanyReport: React.FC<IProps> = ({ title }) => {
  const [companyIds, setCompanyIds] = useState<number[]>([]);
  const [families, setFamilies] = useState<FamilyOption[]>([]);
  const [range, setRange] = useState<[Moment, Moment]>(() => [
    moment()
      .startOf('isoWeek')
      .subtract(DEFAULT_WEEKS - 1, 'weeks'),
    moment(),
  ]);
  const [unit, setUnit] = useState<Unit>('week');
  const [familyInput, setFamilyInput] = useState('');
  const [familyQuery, setFamilyQuery] = useState('');

  // 入力のたびに検索しないよう少し待つ
  useEffect(() => {
    const timer = setTimeout(() => setFamilyQuery(familyInput.trim()), 300);

    return () => clearTimeout(timer);
  }, [familyInput]);

  const companiesQuery = useQuery(['company-report-companies'], () =>
    CompanyReportService.companies(),
  );
  const familySearch = useQuery(
    ['company-report-families', familyQuery],
    () => CompanyReportService.families(familyQuery),
    { enabled: familyQuery.length > 0 },
  );

  const familyIds = families.map((f) => f.value);
  const from = range[0].format('YYYY-MM-DD');
  const to = range[1].format('YYYY-MM-DD');
  const hasTarget = companyIds.length > 0 || familyIds.length > 0;

  const reportQuery = useQuery(
    ['company-report', companyIds, familyIds, from, to],
    () => CompanyReportService.get({ companyIds, familyIds, from, to }),
    { enabled: hasTarget, keepPreviousData: true },
  );
  const report = hasTarget ? reportQuery.data?.data : undefined;

  const buckets = useMemo(
    () =>
      report
        ? toReportBuckets(report.series, unit, moment().format('YYYY-MM-DD'))
        : [],
    [report, unit],
  );

  const companyOptions = (companiesQuery.data?.data ?? []).map((c) => ({
    value: c.companyId,
    label: `${c.name}（${c.activeFamilies}家族）`,
  }));
  const familyOptions = (familySearch.data?.data ?? []).map((f) => ({
    value: f.familyId,
    label: `${f.name ?? '（名前なし）'}（ID ${f.familyId}・${f.members}人）`,
  }));

  return (
    <ConfigProvider theme={SURFACE_THEME}>
      <Helmet>
        <title>{title}</title>
      </Helmet>

      <div className='flex flex-col gap-xl pb-xl'>
        <div>
          <Title level={4} className='!mb-xs'>
            企業別ダッシュボード
          </Title>
          <Text type='secondary' className='text-[12px]'>
            企業（団体コード）や家族を選ぶと、その家族の週ごとの推移と期間内の集計を表示します。週の値は毎朝
            7:30 に今週・先週分を集計（今週は途中経過）。
          </Text>
        </div>

        <Card size='small'>
          <div className='flex flex-wrap items-end gap-md'>
            <div className='flex flex-col gap-2xs min-w-[260px] flex-1'>
              <Text type='secondary' className='text-[12px]'>
                企業
              </Text>
              <Select
                mode='multiple'
                allowClear
                placeholder='企業を選ぶ（期間中に団体コードが適用されていた家族）'
                loading={companiesQuery.isLoading}
                status={companiesQuery.isError ? 'error' : undefined}
                options={companyOptions}
                optionFilterProp='label'
                value={companyIds}
                onChange={setCompanyIds}
              />
              {companiesQuery.isError && (
                <Text type='danger' className='text-[12px]'>
                  企業の一覧を取得できませんでした。
                  <Button
                    type='link'
                    size='small'
                    className='!px-xs'
                    onClick={() => companiesQuery.refetch()}
                  >
                    再読み込み
                  </Button>
                </Text>
              )}
            </div>
            <div className='flex flex-col gap-2xs min-w-[260px] flex-1'>
              <Text type='secondary' className='text-[12px]'>
                家族（個別に追加）
              </Text>
              <Select
                mode='multiple'
                allowClear
                labelInValue
                showSearch
                filterOption={false}
                placeholder='家族名または家族 ID で検索'
                status={familySearch.isError ? 'error' : undefined}
                notFoundContent={
                  familySearch.isFetching ? (
                    <Spin size='small' />
                  ) : familySearch.isError ? (
                    <Text type='danger' className='text-[12px]'>
                      検索できませんでした。
                      <Button
                        type='link'
                        size='small'
                        className='!px-xs'
                        onClick={() => familySearch.refetch()}
                      >
                        再試行
                      </Button>
                    </Text>
                  ) : familyQuery ? (
                    '見つかりません'
                  ) : null
                }
                options={familyOptions}
                value={families}
                onSearch={setFamilyInput}
                // 選んだ家族は名前を出さず ID だけにする
                onChange={(v) =>
                  setFamilies(
                    (v as FamilyOption[]).map((f) => ({
                      value: f.value,
                      label: `家族 ID ${f.value}`,
                    })),
                  )
                }
              />
            </div>
            <div className='flex flex-col gap-2xs'>
              <Text type='secondary' className='text-[12px]'>
                期間
              </Text>
              <RangePicker
                value={range as never}
                onChange={(v) => {
                  const picked = v as unknown as
                    | [Moment | null, Moment | null]
                    | null;
                  if (picked?.[0] && picked[1])
                    setRange([picked[0], picked[1]]);
                }}
              />
            </div>
            <Segmented
              options={UNIT_OPTIONS}
              value={unit}
              onChange={(v) => setUnit(v as Unit)}
            />
          </div>
        </Card>

        {!hasTarget && (
          <Card>
            <Empty description='企業か家族を選んでください' />
          </Card>
        )}

        {hasTarget && reportQuery.isError && (
          <LoadError onRetry={() => reportQuery.refetch()} />
        )}

        {hasTarget && reportQuery.isLoading && (
          <div className='flex justify-center py-3xl'>
            <Spin />
          </div>
        )}

        {report && (
          <Spin spinning={reportQuery.isFetching}>
            <div className='flex flex-col gap-md'>
              <Text type='secondary' className='text-[12px]'>
                集計期間 {report.period.from} 〜 {report.period.to}
                （週の始まりに揃えています）
              </Text>
              {report.families.length === 0 ? (
                <Card>
                  <Empty description='期間中に対象になる家族がいません' />
                </Card>
              ) : (
                <>
                  <div className='flex flex-col gap-sm'>
                    <Text type='secondary' className='text-[12px]'>
                      対象 {report.families.length}家族・
                      {formatValue(report.members, 0, '人')}
                      。このカードと下のユーザー属性は、期間によらず表示した時点の値です。
                    </Text>
                    <ActivityCards report={report} />
                  </div>
                  <IndexChart buckets={buckets} unit={unit} />
                  <div className='grid grid-cols-1 lg:grid-cols-3 gap-md'>
                    <LifestyleCard report={report} />
                    <NouknowCard report={report} />
                    <HenkaCard report={report} />
                  </div>
                  <ArticlesCard report={report} />
                  <DemographicsPanel
                    demographics={report.demographics}
                    asOf={reportQuery.dataUpdatedAt}
                    dimensions={['gender', 'ageBand']}
                    note={({ registered, active }) => (
                      <>
                        登録＝対象家族のメンバー（
                        {formatNumber(registered, '人')}
                        ）。アクティブ＝うち直近30日にアプリを開いた人（
                        {formatNumber(active, '人')}、MAU
                        と同じ）。年齢は表示した日の時点
                      </>
                    )}
                  />
                </>
              )}
            </div>
          </Spin>
        )}
      </div>
    </ConfigProvider>
  );
};
